import { useState } from 'preact/hooks';
import { canSleep } from '../../game/actions';
import { MAX_FARM_NAME_LENGTH } from '../../game/constants';
import { formatClock } from '../../game/simulation';
import { Panel } from '../components/Panel';
import { openPanel } from '../store';
import { useGameState, useServices } from '../context';

export function HousePanel() {
  const state = useGameState();
  const { game } = useServices();
  const [name, setName] = useState(state.farmName);
  const sleepy = canSleep(state.dayTime);
  return (
    <Panel title={state.farmName} icon="🏡">
      <div class="house-sleep">
        <p>
          It's <b>{formatClock(state.dayTime)}</b> on day {state.day}.{' '}
          {sleepy
            ? 'The stars are out — time for bed?'
            : 'Too bright to sleep! Come back after sunset.'}
        </p>
        <button
          class="big-button"
          disabled={!sleepy}
          onClick={() => {
            game.dispatch({ type: 'sleep' });
            openPanel(null);
          }}
        >
          😴 Sleep until morning
        </button>
        <p class="muted small">Watered flowers keep growing while you sleep.</p>
      </div>
      {game.isAuthority && (
        <form
          class="rename"
          onSubmit={(e) => {
            e.preventDefault();
            game.dispatch({ type: 'renameFarm', name });
          }}
        >
          <label class="field">
            <span>Farm name</span>
            <input
              value={name}
              maxLength={MAX_FARM_NAME_LENGTH}
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
            />
          </label>
          <button class="small-button primary" type="submit">
            Rename
          </button>
        </form>
      )}
      <ul class="stats">
        <li>
          🌱 Planted <b>{state.stats.planted}</b>
        </li>
        <li>
          🌸 Harvested <b>{state.stats.harvested}</b>
        </li>
        <li>
          📜 Orders <b>{state.stats.ordersCompleted}</b>
        </li>
        <li>
          🪙 Earned <b>{state.stats.coinsEarned.toLocaleString()}</b>
        </li>
      </ul>
    </Panel>
  );
}

export function SettingsPanel() {
  const { settings, updateSettings, resetFarm, game } = useServices();
  const s = settings.value;
  const [confirm, setConfirm] = useState(false);
  const [initialQuality] = useState(s.quality);
  return (
    <Panel title="Settings" icon="⚙️">
      <label class="field">
        <span>🎵 Music</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={s.musicVolume}
          onInput={(e) =>
            updateSettings({ musicVolume: Number((e.target as HTMLInputElement).value) })
          }
        />
      </label>
      <label class="field">
        <span>🔔 Sound effects</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={s.sfxVolume}
          onInput={(e) =>
            updateSettings({ sfxVolume: Number((e.target as HTMLInputElement).value) })
          }
        />
      </label>
      <label class="field checkbox">
        <input
          type="checkbox"
          checked={s.muted}
          onChange={(e) => updateSettings({ muted: (e.target as HTMLInputElement).checked })}
        />
        <span>Mute everything</span>
      </label>
      <div class="field">
        <span>✨ Graphics</span>
        <div class="segmented" role="radiogroup" aria-label="Graphics quality">
          {(['low', 'high'] as const).map((q) => (
            <button
              key={q}
              role="radio"
              aria-checked={s.quality === q}
              class={s.quality === q ? 'active' : ''}
              onClick={() => updateSettings({ quality: q })}
            >
              {q === 'low' ? 'Speedy' : 'Pretty'}
            </button>
          ))}
        </div>
      </div>
      {s.quality !== initialQuality && (
        <button class="small-button primary" onClick={() => window.location.reload()}>
          Reload to apply graphics change
        </button>
      )}
      <hr />
      {game.mode === 'client' ? (
        <p class="muted small">You're visiting a friend — your own farm is safe at home.</p>
      ) : confirm ? (
        <div class="danger-zone">
          <p>This deletes your farm forever. Are you sure?</p>
          <div class="button-row">
            <button class="small-button danger" onClick={resetFarm}>
              Yes, start over
            </button>
            <button class="small-button" onClick={() => setConfirm(false)}>
              Keep my farm
            </button>
          </div>
        </div>
      ) : (
        <button class="small-button ghost" onClick={() => setConfirm(true)}>
          🗑️ Start a new farm…
        </button>
      )}
    </Panel>
  );
}

export function HelpPanel() {
  return (
    <Panel title="How to Play" icon="❓">
      <ol class="help">
        <li>
          <b>Plant</b> 🌱 — click an empty plot of soil with the ✋ hand to plant your selected
          seed.
        </li>
        <li>
          <b>Water</b> 💧 — flowers only grow while their soil is damp. Thirsty plots show a 💧
          bubble.
        </li>
        <li>
          <b>Harvest</b> 🌸 — click a flower in full bloom. Sometimes you get a seed back!
        </li>
        <li>
          <b>Sell</b> 🏪 — visit the market stand. Prices change every day; the ⭐ featured flower
          pays extra.
        </li>
        <li>
          <b>Orders</b> 📜 — the critters at the order board pay big bonuses.
        </li>
        <li>
          <b>Breed rare flowers</b> 🐝 — harvest a bloom while a <i>different</i> flower blooms next
          to it, and they may cross-pollinate into a rare hybrid seed. Check the 📖 almanac for
          hints!
        </li>
        <li>
          <b>Grow</b> 🏡 — buy more land, sprinklers, a beehive and more. Sleep at night in your
          cottage to skip to morning.
        </li>
        <li>
          <b>Friends</b> 👥 — open your farm and share the code to farm together.
        </li>
      </ol>
      <h3>Controls</h3>
      <ul class="controls">
        <li>
          <kbd>Click</kbd> / <kbd>Tap</kbd> — walk & interact
        </li>
        <li>
          <kbd>WASD</kbd> / arrows — walk
        </li>
        <li>
          <kbd>Space</kbd> — use the plot in front of you
        </li>
        <li>
          <kbd>1</kbd>–<kbd>4</kbd> — pick a tool
        </li>
        <li>
          <kbd>Drag</kbd> / <kbd>Q</kbd> <kbd>E</kbd> — turn the camera · <kbd>Scroll</kbd> / pinch
          — zoom
        </li>
        <li>
          <kbd>Enter</kbd> — chat (multiplayer) · <kbd>Esc</kbd> — close windows
        </li>
      </ul>
    </Panel>
  );
}
